// Inverted index types
export type Key = string;
export type PathIndex = number;
export type Path = readonly PathIndex[];

// Index entries
export interface Entry {
    // Maybe track key later ?
    // key: Key,
    index: PathIndex; // Index relative to it's direct parent
    parentKey: Key; // The node's directy parent
}
export type Pair = [Key, Entry];

/*
 * NOTE: this is a utility method that will probably disappear
 * and be replaced by the next-gen LookupIndex's constructor
 */
export class LookupIndex {
    /** The node that "owns" this LookupIndex */
    private ownerKey: Key;
    private m: Record<Key, Entry>;

    constructor(ownerKey: Key, pairs: Pair[][]) {
        this.ownerKey = ownerKey;
        this.m = {};
        for (let i = 0; i < pairs.length; i++) {
            const ps = pairs[i];
            for (let j = 0; j < ps.length; j++) {
                const p = ps[j];
                const existing = this.m[p[0]];
                if (existing) {
                    throw new Error(
                        `Key "${
                            p[0]
                        }" is already indexed in the LookupIndex, it may indicate a duplicated key in the nodes.\nTrying to set at ${
                            p[1].parentKey
                        }[${p[1].index}] (${this.get(p[1].parentKey)?.join('/')}/${
                            p[1].index
                        }) but it's already set at ${existing.parentKey}[${
                            existing.index
                        }] (${this.get(p[0])?.join('/')})`
                    );
                }

                this.m[p[0]] = p[1];
            }
        }
    }

    /**
     * Get path for a key.
     */
    public get(key: Key): Readonly<Path> | undefined {
        const p: number[] = [];
        let currentKey = key;
        // Start at the provided key and follow the parent's till we reach the owner node
        while (currentKey !== this.ownerKey) {
            const entry = this.m[currentKey];
            if (!entry) {
                return undefined;
            }
            currentKey = entry.parentKey;
            p.push(entry.index);
        }

        p.reverse();

        // Freeze path to avoid mutations as paths can be passed as argument to memoized functions.
        return Object.freeze(p);
    }

    /**
     * Check if a key is in the index.
     */
    public has(key: Key): boolean {
        return this.m[key] !== undefined;
    }

    /**
     * Return all keys in the index.
     */
    public keys(): Key[] {
        return Object.keys(this.m);
    }
}

/*
 * Path methods
 * NOTE: these are not yet used today, but will be used in future
 * iterations of the LookupIndex and node-factory
 */

function pathCompare(a: Path, b: Path): number {
    const commonLength = Math.min(a.length, b.length);
    // Compare common parts of paths and exit if we see a non equality
    for (let i = 0; i < commonLength; i++) {
        if (a[i] > b[i]) {
            return 1;
        }
        if (a[i] < b[i]) {
            return -1;
        }
    }
    // Paths are equal
    if (a.length === b.length) {
        return 0;
    }
    // Common parts are equal, so the longer path is ahead of the other
    return a.length > b.length ? 1 : -1;
}

export function isPathEqual(a: Path, b: Path): boolean {
    return pathCompare(a, b) === 0;
}

export function isPathBefore(a: Path, b: Path): boolean {
    return pathCompare(a, b) === -1;
}

export function isPathAfter(a: Path, b: Path): boolean {
    return pathCompare(a, b) === 1;
}

export function isParentPathEqual(a: Path, b: Path): boolean {
    return a.length === b.length && isPathEqual(a.slice(0, -1), b.slice(0, -1));
}
